import { useState } from 'react';
import { draftField, useDraft } from '../../offline/draft.js';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { TICKET_CATEGORIES } from '@nu/shared';
import PhotoPicker from '../../components/PhotoPicker.jsx';
import { isNative } from '../../config.js';
import { useOnline, useOutboxCounts } from '../../offline/index.js';
import { pushState } from '../../native/push.js';
import { useAppUpdate, versionText } from '../../native/updates.js';
import { raiseTicket } from './store.js';

// Log a ticket: what it's about, a title, what's wrong, whether it's
// stopping them working, and photos or screenshots. Works with no signal
// (it sends when there is). The app's version, connection and anything stuck
// waiting to send go with it, so the helpdesk doesn't have to ask.
export default function NewTicket({ me, shell }) {
  const navigate = useNavigate();
  const online = useOnline();
  const { pending, rejected } = useOutboxCounts();
  const update = useAppUpdate();
  // What's typed is kept on the phone until it's sent (photos aren't).
  const [draft, saveDraft, clearDraft] = useDraft('nu.draft.ticket.new');
  const [category, setCategory] = draftField(draft, saveDraft, 'category', '');
  const [subject, setSubject] = draftField(draft, saveDraft, 'subject', '');
  const [body, setBody] = draftField(draft, saveDraft, 'body', '');
  const [stopping, setStopping] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const ready = category && subject.trim().length >= 3 && body.trim().length >= 2 && stopping !== null && !adding && !busy;
  const send = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    const device = {
      app: versionText(update.native),
      platform: isNative ? 'Android app' : 'Browser',
      view: shell === 'field' ? 'Field' : 'Office',
      online,
      waitingToSend: pending,
      needAttention: rejected,
      notifications: pushState(),
      browser: navigator.userAgent,
    };
    const id = await raiseTicket(me, { category, subject: subject.trim(), body: body.trim(), stopping, device }, photos.map((p, i) => ({ blob: p.blob, name: `photo-${i + 1}.jpg` })));
    clearDraft();
    navigate(`/helpdesk/${id}`, { replace: true });
  };

  return (
    <form className="card ticket-new" onSubmit={send}>
      <button type="button" className="link-btn back-link" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} aria-hidden="true" /> Back
      </button>
      <h1>Log a ticket</h1>
      <p className="muted">Tell the helpdesk what&apos;s wrong or what you need. You&apos;ll get a notification when they reply.</p>

      <fieldset className="ticket-cats">
        <legend>What is it about? <span className="req">(required)</span></legend>
        {TICKET_CATEGORIES.map((c) => (
          <label key={c.value} className={`ticket-cat${category === c.value ? ' on' : ''}`}>
            <input type="radio" name="category" value={c.value} checked={category === c.value} onChange={() => setCategory(c.value)} />
            <strong>{c.label}</strong>
            <span className="small muted">{c.hint}</span>
          </label>
        ))}
      </fieldset>

      <label className="fld">
        Title <span className="req small">(required)</span>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="e.g. Photos won’t upload on my job" />
      </label>
      <label className="fld">
        What&apos;s happening? <span className="req small">(required)</span>
        <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} placeholder="What you were doing, what went wrong, any error message" />
      </label>

      <div className="fld">
        <span>Is it stopping you working? <span className="req small">(required)</span></span>
        <div className="reason-chips left" role="radiogroup" aria-label="Is it stopping you working?">
          {[[true, 'Yes'], [false, 'No']].map(([v, l]) => (
            <button key={l} type="button" role="radio" aria-checked={stopping === v} className={`chip${stopping === v ? ' on' : ''}`} onClick={() => setStopping(v)}>{l}</button>
          ))}
        </div>
      </div>

      <PhotoPicker photos={photos} setPhotos={setPhotos} onBusy={setAdding} label="Photos or screenshots" />

      <div className="actions">
        <button type="submit" className="btn primary" disabled={!ready}>{busy ? 'Sending…' : 'Send ticket'}</button>
      </div>
      {!online && <p className="small muted">You&apos;re offline. The ticket is saved on this device and sends when you have signal.</p>}
      <p className="small muted">Your app version and connection details are sent with it.</p>
    </form>
  );
}
